import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowBack, Download, OpenInNew } from '@mui/icons-material';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
  backgroundColor: '#ffffff',
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 51, 102, 0.1)',
}));

const StyledMarkdown = styled('div')(({ theme }) => ({
  '& h1': {
    color: '#003366',
    fontSize: '2.5rem',
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    borderBottom: `3px solid #003366`,
    paddingBottom: theme.spacing(1),
  },
  '& h2': {
    color: '#003366',
    fontSize: '1.8rem',
    fontWeight: 600,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  '& h3': {
    color: '#003366',
    fontSize: '1.4rem',
    fontWeight: 600,
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
  },
  '& h4': {
    color: '#003366',
    fontSize: '1.2rem',
    fontWeight: 600,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  '& p': {
    fontSize: '1rem',
    lineHeight: 1.7,
    marginBottom: theme.spacing(2),
    color: '#333333',
  },
  '& ul, & ol': {
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
  },
  '& li': {
    fontSize: '1rem',
    lineHeight: 1.6,
    marginBottom: theme.spacing(1),
    color: '#333333',
  },
  '& strong': {
    color: '#003366',
    fontWeight: 600,
  },
  '& blockquote': {
    borderLeft: `4px solid #003366`,
    paddingLeft: theme.spacing(2),
    margin: theme.spacing(2, 0),
    fontStyle: 'italic',
    backgroundColor: '#f8f9fa',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  '& code': {
    backgroundColor: '#f1f3f4',
    padding: '2px 6px',
    borderRadius: '4px',
    fontFamily: 'Courier New, monospace',
    fontSize: '0.9rem',
  },
  '& pre': {
    backgroundColor: '#f1f3f4',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    overflowX: 'auto',
    marginBottom: theme.spacing(2),
  },
  '& pre code': {
    backgroundColor: 'transparent',
    padding: 0,
  },
  '& hr': {
    border: 'none',
    borderTop: `2px solid #e0e0e0`,
    margin: theme.spacing(3, 0),
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: theme.spacing(2),
  },
  '& th, & td': {
    border: '1px solid #e0e0e0',
    padding: theme.spacing(1),
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: '#003366',
    color: '#ffffff',
    fontWeight: 600,
  },
}));

const DevSecOpsUseCase: React.FC = () => {
  const navigate = useNavigate();
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const handleBack = () => {
    navigate('/devsecops');
  };

  const handleDownloadMarkdown = () => {
    const blob = new Blob([markdownContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'odos-iv-devsecops-proposal-usecase.md';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOpenInNewTab = () => {
    const newWindow = window.open('/docs/usecases/devsecops-proposal-usecase.md', '_blank');
    if (newWindow) {
      newWindow.focus();
    }
  };

  useEffect(() => {
    const fetchUseCase = async () => {
      try {
        setLoading(true);
        // Fetch the markdown file from the public/docs directory
        const response = await fetch('/docs/usecases/devsecops-proposal-usecase.md');
        
        if (!response.ok) {
          throw new Error('Use case document not found');
        }
        
        const content = await response.text();
        setMarkdownContent(content);
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load use case document';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchUseCase();
  }, []);

  // Simple markdown parser (basic implementation)
  const parseMarkdown = (markdown: string): string => {
    return markdown
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
      // Italic
      .replace(/\*(.*)\*/gim, '<em>$1</em>')
      // Code blocks
      .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // Line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br />')
      // Wrap in paragraphs
      .replace(/^(.+)$/gm, '<p>$1</p>')
      // Clean up empty paragraphs
      .replace(/<p><\/p>/g, '')
      .replace(/<p><h/g, '<h')
      .replace(/<\/h([1-6])><\/p>/g, '</h$1>')
      .replace(/<p><pre>/g, '<pre>')
      .replace(/<\/pre><\/p>/g, '</pre>')
      .replace(/<p><ul>/g, '<ul>')
      .replace(/<\/ul><\/p>/g, '</ul>')
      .replace(/<p><ol>/g, '<ol>')
      .replace(/<\/ol><\/p>/g, '</ol>')
      .replace(/<p><li>/g, '<li>')
      .replace(/<\/li><\/p>/g, '</li>')
      .replace(/<p><blockquote>/g, '<blockquote>')
      .replace(/<\/blockquote><\/p>/g, '</blockquote>')
      .replace(/<p><hr \/><\/p>/g, '<hr />');
  };

  const renderMarkdown = () => {
    const htmlContent = parseMarkdown(markdownContent);
    return <StyledMarkdown dangerouslySetInnerHTML={{ __html: htmlContent }} />;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/')}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Home
          </Link>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/devsecops')}
            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            DevSecOps
          </Link>
          <Typography color="text.primary">Use Case</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#003366' }}>
            ODOS IV DevSecOps Proposal Use Case
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadMarkdown}
              disabled={!markdownContent}
            >
              Download
            </Button>
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={handleOpenInNewTab}
            >
              Open in New Tab
            </Button>
            <Button
              variant="contained"
              startIcon={<ArrowBack />}
              onClick={handleBack}
              sx={{
                backgroundColor: '#003366',
                '&:hover': { backgroundColor: '#002244' },
              }}
            >
              Back to DevSecOps
            </Button>
          </Box>
        </Box>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={60} sx={{ color: '#003366' }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          <StyledPaper>
            {renderMarkdown()}
          </StyledPaper>
        )}
      </Box>
    </Container>
  );
};

export default DevSecOpsUseCase;
